import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getMyReceiptView,
  buildAffiliateReceiptManifest,
  type OwnerReceiptView,
} from "@/lib/affiliate/receipt.functions";
import { Button } from "@/components/ui/button";
import { sanitizeAgreementHtml } from "@/lib/affiliate/sanitize-html";

export const Route = createFileRoute("/affiliate/receipt")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    n: typeof s.n === "string" ? s.n.slice(0, 32) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Your affiliate application receipt" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReceiptPage,
});

function ReceiptPage() {
  const search = Route.useSearch();
  const receiptNumber = search.n;
  const [state, setState] = useState<OwnerReceiptView | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchReceipt = useServerFn(getMyReceiptView);
  const buildManifest = useServerFn(buildAffiliateReceiptManifest);

  useEffect(() => {
    if (!receiptNumber || !/^TW-AF-\d{8}-[A-Z0-9]{6}$/.test(receiptNumber)) {
      setState({ ok: false, reason: "not_found" });
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await fetchReceipt({ data: { receipt_number: receiptNumber } });
        setState(r);
      } catch {
        setState({ ok: false, reason: "not_found" });
      } finally {
        setLoading(false);
      }
    })();
  }, [receiptNumber, fetchReceipt]);

  const acceptedUtc = useMemo(
    () => (state?.ok ? new Date(state.accepted_at).toUTCString() : ""),
    [state],
  );
  const acceptedIst = useMemo(
    () =>
      state?.ok
        ? new Date(new Date(state.accepted_at).getTime() + 5.5 * 3600_000)
            .toISOString()
            .replace("Z", "+05:30")
        : "",
    [state],
  );

  async function downloadManifest() {
    if (!receiptNumber) return;
    const r = await buildManifest({ data: { receipt_number: receiptNumber } });
    if (!r.ok) return;
    const blob = new Blob([JSON.stringify(r.manifest, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${receiptNumber}-manifest.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return <Shell><p className="text-sm text-muted-foreground">Loading receipt…</p></Shell>;
  }
  if (!state || !state.ok) {
    return (
      <Shell>
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-medium">Receipt not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The receipt could not be located, or you may not have permission to view it.
            <br />
            <Link to="/affiliate/apply" className="text-primary underline">Return to the affiliate application</Link>.
          </p>
        </div>
      </Shell>
    );
  }

  const labels = state.consent_labels || {};
  return (
    <Shell>
      <div className="rounded-lg border border-border bg-card p-6 print:border-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">TruWe Affiliate Programme</p>
            <h1 className="mt-1 text-2xl font-semibold">Application receipt</h1>
          </div>
          <div className="print:hidden flex gap-2">
            <Button variant="outline" onClick={() => window.print()}>Print</Button>
            <Button onClick={downloadManifest}>Download proof</Button>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Row k="Receipt number" v={state.receipt_number} mono />
          <Row k="Agreement version" v={state.terms_version} mono />
          <Row k="Content SHA-256" v={state.terms_sha256} mono small />
          <Row k="Accepted at (UTC)" v={acceptedUtc} />
          <Row k="Accepted at (IST)" v={acceptedIst} />
          <Row k="Verified WhatsApp" v={state.phone_masked} mono />
          <Row k="Locale" v={state.locale} />
          <Row k="Email delivery" v={state.email_status} />
        </dl>

        <section className="mt-8">
          <h2 className="text-base font-medium">Consents recorded</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-6 text-sm">
            {["agreement", "privacy", "asci"].map((k) => (
              <li key={k}>{labels[k] ?? <em>Missing label</em>}</li>
            ))}
          </ol>
        </section>

        <section className="mt-8">
          <h2 className="text-base font-medium">Accepted agreement</h2>
          {state.content_html ? (
            <div
              className="prose prose-neutral mt-3 max-w-none text-sm"
              // Server-generated content only. Re-sanitized at render time as
              // defense-in-depth (see finding 13) in case a compromised session or
              // a future regression ever wrote unsanitized content upstream.
              dangerouslySetInnerHTML={{ __html: sanitizeAgreementHtml(state.content_html) }}
            />
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              The accepted agreement text has been archived. The version identifier and
              SHA-256 above uniquely fingerprint the exact document you accepted.
            </p>
          )}
        </section>

        <p className="mt-8 text-xs text-muted-foreground">
          This receipt is admissible as an electronic record under the Information
          Technology Act, 2000 (ss. 4, 7, 10A, 11-13) and the Bharatiya Sakshya
          Adhiniyam, 2023 (ss. 61-63). The Affiliate Partner Agreement referenced above
          is a preview draft pending independent review by Indian counsel; no
          partnership is active at the time of this receipt.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto max-w-3xl px-6 py-12">{children}</main>;
}

function Row({ k, v, mono, small }: { k: string; v: string; mono?: boolean; small?: boolean }) {
  return (
    <div className="flex flex-col rounded-md border border-border/60 bg-muted/20 px-3 py-2">
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</dt>
      <dd className={`${mono ? "font-mono" : ""} ${small ? "break-all text-[11px]" : "text-sm"}`}>{v}</dd>
    </div>
  );
}
